import { useState, useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AIAssistant = forwardRef(function AIAssistant(_, ref) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(localStorage.getItem("mfi-session") || (() => {
    const id = crypto.randomUUID();
    localStorage.setItem("mfi-session", id);
    return id;
  })());

  useImperativeHandle(ref, () => ({
    open: () => setOpen(true),
  }));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    if (messages.length === 0) setMessages([{ role: "assistant", content: t.chat.greeting }]);
    else setMessages((m) => (m.length === 1 && m[0].role === "assistant" ? [{ role: "assistant", content: t.chat.greeting }] : m));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: msg, language: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let errored = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") break;
          if (data === "[ERROR]") { errored = true; break; }
          const token = data.replace(/\\n/g, "\n");
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
            return copy;
          });
        }
        if (errored) break;
      }
      if (errored) {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            data-testid="ai-chat-trigger-floating-button" onClick={() => setOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#C85A17] text-[#FDFCF7] shadow-[0_12px_32px_-8px_rgba(200,90,23,0.55)] flex items-center justify-center hover:bg-[#a84a12] transition-colors duration-200"
            aria-label={t.chat.title}>
            <Sparkles className="w-6 h-6" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] h-[560px] max-h-[calc(100vh-3rem)] rounded-2xl border border-[#E7DFD5] bg-white shadow-[0_32px_80px_-24px_rgba(23,24,26,0.4)] flex flex-col overflow-hidden"
            data-testid="ai-chat-panel">
            <div className="flex items-center justify-between px-5 py-4 bg-[#163832] text-[#FDFCF7]">
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-[#C85A17] flex items-center justify-center">
                  <Sparkles className="w-4.5 h-4.5 w-5 h-5" />
                </span>
                <div>
                  <div className="font-display font-semibold text-sm" data-testid="ai-chat-title">{t.chat.title}</div>
                  <div className="text-xs text-[#FDFCF7]/60">{t.chat.subtitle}</div>
                </div>
              </div>
              <button data-testid="ai-chat-close-button" onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" aria-label="Close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAF7F2]" data-testid="ai-chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#C85A17] text-[#FDFCF7] rounded-br-md" : "bg-white border border-[#E7DFD5] text-[#17181A] rounded-bl-md"
                  }`}>
                    {m.content || (streaming && i === messages.length - 1 ? t.chat.thinking : "")}
                  </div>
                </div>
              ))}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 bg-[#FAF7F2] flex flex-wrap gap-2">
                {t.chat.prompts.map((p, i) => (
                  <button key={i} data-testid={`ai-chat-quick-prompt-${i}`} onClick={() => send(p)}
                    className="text-xs border border-[#C85A17]/40 text-[#C85A17] rounded-full px-3 py-1.5 hover:bg-[#FFF3EB] transition-colors duration-150">
                    {p}
                  </button>
                ))}
              </div>
            )}

            <div className="px-4 py-3 border-t border-[#E7DFD5] bg-white">
              <div className="flex items-center gap-2">
                <input data-testid="ai-chat-input-field" value={input} onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t.chat.placeholder} disabled={streaming}
                  className="flex-1 text-sm bg-[#FAF7F2] border border-[#E7DFD5] rounded-full px-4 py-2.5 outline-none focus:border-[#C85A17] transition-colors disabled:opacity-60" />
                <button data-testid="ai-chat-submit-button" onClick={() => send()} disabled={streaming || !input.trim()}
                  className="w-10 h-10 rounded-full bg-[#C85A17] text-[#FDFCF7] flex items-center justify-center hover:bg-[#a84a12] transition-colors disabled:opacity-50" aria-label="Send">
                  {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-[10px] text-[#5F6368]/70 mt-2 text-center">{t.chat.disclaimer}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});
